import { Button, Card, CardContent, TextField, Typography } from '@material-ui/core';
import { useState } from 'react';
import { CanvasBar } from '../../../layout/Canvas';
import { Page } from '../../../layout/Page';
import { Section } from '../../../layout/Section';
import { callFirebaseFunction } from '../../../services/firebaseFunctions';

/**
 * Superadmin "Template Copy" tool.
 *
 * Export reads + structure-only-scrubs a template from THIS environment and
 * gives you a PII-free JSON. Import writes a reviewed JSON into THIS
 * environment under a new id + destination org. The JSON is the bridge between
 * environments — export in the source env (e.g. prod), download, then import in
 * the target env (e.g. dev). The operator's per-env superadmin access is the
 * gate: no access in an env -> that half 401s.
 */

interface ScrubbedTemplateExport {
  sourceTemplateId: string;
  sourceOrgId: string;
  sourceStack: 'v1' | 'v2';
  scrubbedData: Record<string, unknown>;
  ruleValueMap: Record<string, string>;
  report: { replacedCount: number; changes: string[]; keptSample: string[] };
  residualPiiToReview: string[];
  danglingRefs: string[];
}

const errMessage = (e: unknown): string =>
  (e as { message?: string })?.message || String(e);

const downloadJson = (filename: string, text: string): void => {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const monospaceBox: React.CSSProperties = {
  width: '100%',
  minHeight: 180,
  fontFamily: 'monospace',
  fontSize: 12,
  whiteSpace: 'pre',
  overflow: 'auto'
};

const Export = () => {
  const [orgId, setOrgId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ScrubbedTemplateExport | null>(null);

  const run = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await callFirebaseFunction<ScrubbedTemplateExport>(
        'admin_exportScrubbedTemplate',
        { orgId: orgId.trim(), templateId: templateId.trim() }
      );
      setResult(res);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const json = result ? JSON.stringify(result, null, 2) : '';

  return (
    <Section>
      <CanvasBar>Export &amp; scrub (reads THIS environment)</CanvasBar>
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Reads <code>experienceTemplatesV1/&lt;templateId&gt;</code> from the
            environment you're signed into, validates org ownership, and
            structure-only-scrubs all free-text (preserving{' '}
            <code>{'{{ tokens }}'}</code>). Download the JSON and import it in the
            target environment.
          </Typography>
          <TextField
            label="Source org id"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Template id"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            fullWidth
            margin="dense"
          />
          <div style={{ marginTop: 12 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || !orgId.trim() || !templateId.trim()}
              onClick={run}
            >
              {loading ? 'Exporting…' : 'Export & scrub'}
            </Button>
          </div>

          {error && (
            <Typography color="error" style={{ marginTop: 12 }}>
              {error}
            </Typography>
          )}

          {result && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2">
                Scrubbed {result.report.replacedCount} text value(s).
              </Typography>
              {result.residualPiiToReview.length > 0 && (
                <Typography color="error" style={{ marginTop: 8 }}>
                  ⚠ {result.residualPiiToReview.length} residual PII item(s)
                  detected — import will be REFUSED until you resolve these in
                  the JSON:
                  <br />
                  {result.residualPiiToReview.join(', ')}
                </Typography>
              )}
              {result.danglingRefs.length > 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 8 }}
                >
                  ℹ {result.danglingRefs.length} dangling external ref(s)
                  (expected; won't resolve in dev): {result.danglingRefs.join('; ')}
                </Typography>
              )}
              <div style={{ marginTop: 12, marginBottom: 8 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    downloadJson(`${result.sourceTemplateId}.scrubbed.json`, json)
                  }
                >
                  Download JSON
                </Button>
              </div>
              <textarea readOnly value={json} style={monospaceBox} />
            </div>
          )}
        </CardContent>
      </Card>
    </Section>
  );
};

const Import = () => {
  const [destOrgId, setDestOrgId] = useState('');
  const [jsonText, setJsonText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTemplateId, setNewTemplateId] = useState('');

  const run = async () => {
    setLoading(true);
    setError('');
    setNewTemplateId('');
    let payload: ScrubbedTemplateExport;
    try {
      payload = JSON.parse(jsonText);
    } catch {
      setError('Pasted text is not valid JSON.');
      setLoading(false);
      return;
    }
    if ((payload.residualPiiToReview ?? []).length > 0) {
      setError(
        `This payload still has ${payload.residualPiiToReview.length} residual PII item(s) flagged. Resolve them in the JSON before importing.`
      );
      setLoading(false);
      return;
    }
    try {
      const res = await callFirebaseFunction<{ newTemplateId: string }>(
        'admin_importScrubbedTemplate',
        { payload, destOrgId: destOrgId.trim() }
      );
      setNewTemplateId(res.newTemplateId);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section>
      <CanvasBar>Import (writes THIS environment)</CanvasBar>
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Paste a scrubbed export JSON and pick a destination org. Writes a new
            template (fresh id, remapped org) into the environment you're signed
            into. Refuses if the JSON still has residual PII flagged.
          </Typography>
          <TextField
            label="Destination org id"
            value={destOrgId}
            onChange={(e) => setDestOrgId(e.target.value)}
            fullWidth
            margin="dense"
          />
          <textarea
            placeholder="Paste scrubbed export JSON here"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            style={{ ...monospaceBox, marginTop: 8 }}
          />
          <div style={{ marginTop: 12 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || !destOrgId.trim() || !jsonText.trim()}
              onClick={run}
            >
              {loading ? 'Importing…' : 'Import'}
            </Button>
          </div>

          {error && (
            <Typography color="error" style={{ marginTop: 12 }}>
              {error}
            </Typography>
          )}
          {newTemplateId && (
            <Typography style={{ marginTop: 12 }}>
              ✓ Imported as <code>{newTemplateId}</code> in org{' '}
              <code>{destOrgId.trim()}</code>.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Section>
  );
};

export const PageTemplateCopy = () => (
  <Page>
    <Export />
    <Import />
  </Page>
);
